import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { NotePencilIcon } from '@phosphor-icons/react';
import type { GetPricelistResponse, PricelistCaseResponse } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { useToast } from 'src/context/ToastContext';
import { Label } from '@/components/ui/label';
import { AssigneePicker } from './AssigneePicker';
import { PricelistCaseSetNextStepDialog } from './PricelistCaseSetNextStepDialog';
import { PricelistCaseAudienceSection } from './PricelistCaseAudienceSection';
import { PricelistCaseFilesReadyDialog } from './PricelistCaseFilesReadyDialog';
import { invalidatePricelistCaseQueries } from './pricelistCaseQueryInvalidation';
import { PricelistCaseVendorsField } from './PricelistCaseVendorsField';
import { PricelistCaseSelectedFileActions } from './PricelistCaseSelectedFileActions';
import { PricelistSidebarActionButton } from './PricelistSidebarActionButton';
import { PricelistSidebarShell } from './PricelistSidebarShell';

function ignoreFileDeleted() {}

function PricelistCaseFilesReadyAction({
  pricelistCase,
}: {
  pricelistCase: PricelistCaseResponse;
}) {
  const [isSettingFilesReady, setIsSettingFilesReady] = useState(false);
  return (
    <>
      <PricelistSidebarActionButton
        icon={<NotePencilIcon className="h-4 w-4" />}
        onClick={() => setIsSettingFilesReady(true)}
      >
        <T context="price change sidebar: schedule when vendor files will be ready to upload">
          Schedule Upload Reminder
        </T>
      </PricelistSidebarActionButton>
      <PricelistCaseFilesReadyDialog
        key={`${pricelistCase.id}-${isSettingFilesReady}`}
        pricelistCase={isSettingFilesReady ? pricelistCase : null}
        onOpenChange={setIsSettingFilesReady}
      />
    </>
  );
}

/**
 * Every write a person makes against one price change, in one place: its
 * owner, what it is waiting on, and which vendor it is for.
 *
 * Deliberately the same shell as `PricelistActionsSidebar` — the collapsible
 * `w-72` right panel with stacked full-width actions — because a price change
 * is reached from the pricelist pages, and two different homes for "the
 * actions" across two pages of one module is the kind of thing people learn
 * once per page instead of once.
 *
 * Assignment writes the COMPANION TASK (`cases.assign` → its
 * `assignee_user_id`), never the case: the task owns the work, so it owns the
 * owner. The vendor writes the CASE (`cases.update` → `company_id`). Neither
 * writes history by hand — the `core_tasks` and `im_pricelist_cases` audit
 * triggers record both diffs, which is why the timeline is invalidated
 * alongside the case.
 */
export function PricelistCaseActionsSidebar({
  pricelistCase,
  selectedPricelist,
  onFileDeleted,
}: {
  pricelistCase: PricelistCaseResponse;
  selectedPricelist?: GetPricelistResponse;
  onFileDeleted?: () => void;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isSettingNextStep, setIsSettingNextStep] = useState(false);
  const assignMutation = useMutation(
    orpc.inventoryManagement.pricelistCases.assign.mutationOptions(),
  );

  function invalidateCase() {
    invalidatePricelistCaseQueries(queryClient);
  }

  function failed(title: string, error: unknown) {
    showToast({
      title,
      message:
        error instanceof Error
          ? error.message
          : gt('Something went wrong.', {
              context: 'generic toast message for an unexpected failure',
            }),
      color: 'error',
    });
  }

  async function handleAssign(assigneeUserId: string | null) {
    try {
      await assignMutation.mutateAsync({
        caseId: pricelistCase.id,
        assigneeUserId,
      });
      invalidateCase();
    } catch (error) {
      failed(
        gt('Could not change the owner', {
          context: 'toast title when assigning a pricelist case fails',
        }),
        error,
      );
    }
  }

  return (
    <>
      <PricelistSidebarShell
        heading={<T context="price change actions sidebar heading">Actions</T>}
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pricelist-case-owner" className="text-xs">
              <T context="field label for who owns a pricelist update case">
                Owner
              </T>
            </Label>
            <AssigneePicker
              triggerId="pricelist-case-owner"
              value={pricelistCase.task.assigneeUserId}
              onChange={(userId) => void handleAssign(userId)}
              disabled={
                assignMutation.isPending || pricelistCase.task.taskDeleted
              }
            />
          </div>

          <PricelistCaseAudienceSection pricelistCase={pricelistCase} />

          <PricelistCaseVendorsField
            pricelistCase={pricelistCase}
            onFailed={failed}
          />

          <div className="space-y-2">
            <PricelistSidebarActionButton
              icon={<NotePencilIcon className="h-4 w-4" />}
              onClick={() => setIsSettingNextStep(true)}
            >
              <T context="price change sidebar: record what the case is waiting on">
                Set Next Step
              </T>
            </PricelistSidebarActionButton>
            <PricelistCaseFilesReadyAction pricelistCase={pricelistCase} />
          </div>

          {selectedPricelist ? (
            <PricelistCaseSelectedFileActions
              pricelist={selectedPricelist}
              onDeleted={onFileDeleted ?? ignoreFileDeleted}
            />
          ) : null}
        </div>
      </PricelistSidebarShell>

      <PricelistCaseSetNextStepDialog
        pricelistCase={isSettingNextStep ? pricelistCase : null}
        onOpenChange={setIsSettingNextStep}
      />
    </>
  );
}
